import { RefObject, useCallback, useEffect, useRef, useState } from 'react';

type UseIntersectionObserverOptions<T extends HTMLElement> = {
  ref: RefObject<T>;
  threshold?: IntersectionObserverInit['threshold'];
  root?: IntersectionObserverInit['root'];
  rootMargin?: IntersectionObserverInit['rootMargin'];
  onChange?: (entry: IntersectionObserverEntry) => void;
};

const useIntersectionObserver = <T extends HTMLElement>({
  ref,
  threshold,
  root = null,
  rootMargin,
  onChange,
}: UseIntersectionObserverOptions<T>): boolean => {
  const [isIntersecting, setIsIntersecting] = useState(true);
  const observerRef = useRef<IntersectionObserver>();

  const handleEntries = useCallback<IntersectionObserverCallback>(entries => {
    entries.forEach(entry => {
      setIsIntersecting(entry.isIntersecting);
      onChange?.(entry);
    });
  }, [onChange]);

  useEffect(() => {
    const node = ref.current;
    if (!node) return undefined;

    observerRef.current?.disconnect();
    observerRef.current = new IntersectionObserver(handleEntries, { threshold, root, rootMargin });
    observerRef.current.observe(node);

    return () => observerRef.current?.disconnect();
  }, [ref, root, rootMargin, threshold, handleEntries]);

  return isIntersecting;
};

export default useIntersectionObserver;
